import React, { useCallback } from "react";
import { Button } from "src/components/ui/button";
import { JSONEditor } from "src/components/ui/json-editor";
import { Label } from "src/components/ui/label";

import {
  addNestedParameter,
  createParameter,
  type Parameter,
  parametersToSchema,
  removeParameter,
  schemaToParameters,
  updateParameter,
} from "../../../utils/parameterSchema";
import { ParameterTable } from "../../ParameterTable";
import { type WizardState } from "../types";

interface Step2ParametersProps {
  state: WizardState;
  onUpdate: (updates: Partial<WizardState>) => void;
}

export function Step2Parameters({ state, onUpdate }: Step2ParametersProps) {
  const handleAddParameter = useCallback(() => {
    onUpdate({ parameters: [...state.parameters, createParameter()] });
  }, [state.parameters, onUpdate]);

  const handleAddNestedParameter = useCallback(
    (parentId: string) => {
      onUpdate({ parameters: addNestedParameter(state.parameters, parentId) });
    },
    [state.parameters, onUpdate]
  );

  const handleRemoveParameter = useCallback(
    (id: string) => {
      onUpdate({ parameters: removeParameter(state.parameters, id) });
    },
    [state.parameters, onUpdate]
  );

  const handleParameterChange = useCallback(
    (id: string, field: keyof Parameter, value: unknown) => {
      onUpdate({ parameters: updateParameter(state.parameters, id, field, value) });
    },
    [state.parameters, onUpdate]
  );

  const toggleExpanded = useCallback(
    (paramId: string) => {
      const newExpanded = new Set(state.expandedParams);
      if (newExpanded.has(paramId)) {
        newExpanded.delete(paramId);
      } else {
        newExpanded.add(paramId);
      }
      onUpdate({ expandedParams: newExpanded });
    },
    [state.expandedParams, onUpdate]
  );

  const handleToggleAdvanced = () => {
    if (!state.showAdvanced) {
      // Switching to advanced mode: convert parameters to JSON
      const schema = parametersToSchema(state.parameters);
      onUpdate({ jsonSchema: JSON.stringify(schema, null, 2), showAdvanced: true });
    } else {
      // Switching back to table view: parse JSON back to parameters
      try {
        const schema = JSON.parse(state.jsonSchema);
        const parsedParameters = schemaToParameters(schema);
        onUpdate({ parameters: parsedParameters, showAdvanced: false });
      } catch {
        // If JSON is invalid, just switch view without updating parameters
        onUpdate({ showAdvanced: false });
      }
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Label className="text-muted-foreground text-sm font-medium">Parameters</Label>
        <p className="text-sm text-muted-foreground mb-3">
          Define input parameters that should be used for the pipeline when it runs.
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-end">
          <Button variant="outline" size="sm" onClick={handleToggleAdvanced} type="button">
            {state.showAdvanced ? "Hide" : "Show"} JSON Schema
          </Button>
        </div>

        {state.showAdvanced ? (
          <JSONEditor
            value={state.jsonSchema}
            onChange={(value) => onUpdate({ jsonSchema: value })}
            height="540px"
            placeholder='{"type": "object", "properties": {...}}'
          />
        ) : (
          <ParameterTable
            parameters={state.parameters}
            expandedParams={state.expandedParams}
            onToggleExpand={toggleExpanded}
            onParameterChange={handleParameterChange}
            onRemoveParameter={handleRemoveParameter}
            onAddNestedParameter={handleAddNestedParameter}
            onAddParameter={handleAddParameter}
          />
        )}
      </div>
    </div>
  );
}
